import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { usePHRItems, useTHRItems } from '@/hooks';
import { useCreateLossRecord } from '@/hooks/mutations/asset-loss-management';
import { EXCLUDE_PNP_TYPE_KEEP_NULL } from '@/utils/iemFilters';
import { useNotifications } from '@/stores/useNotificationStore';
import { entityService } from '@/services/EntityService';
import { documentService } from '@/services/documents';
import type { DocumentPreviewResponse } from '@/services/documents';
import { lossRecordService } from '@/services/lossRecordService';
import { openBase64PdfInWindow, openDocumentWithAuth, preOpenWindow } from '@/utils/pdfHelpers';
import { DocumentFieldsModal } from '@/components/documents/DocumentFieldsModal';
import { toYYYYMMDD } from '@/utils/dateFormat';
import { ViewerGuardedButton } from '@/components/shared/ViewerGuardedButton';
import { useTableSort } from '@/hooks/useTableSort';
import { SortableTableCell } from '@/components/shared/SortableTableCell';
import { logger } from '@equip/utils/logger';
import { getLossTypeLabel } from './lossTypeLabels';
import { usePersonLabels } from '@/hooks/usePersonLabels';

const log = logger.child({ component: 'DD200Modal' });

interface PersonOption {
  id: string;
  display_name: string;
  entity_identifier: string | null | undefined;
  rank: string | null;
  unit: string | null;
  lastName: string | null;
  firstName: string | null;
  middleInitial: string | null;
  ssn: string | null;
  section: string | null;
}

interface HandReceiptItem {
  id: string;
  asset_id: string | null;
  item: string;
  lin: string | null;
  nsn: string | null;
  receiptType: 'PHR' | 'THR';
  issueDate: string | null;
  actualPrice: number | null;
  unitPrice: number | null;
}

export interface DD200ModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

export const DD200Modal: React.FC<DD200ModalProps> = ({ open, onClose, onSuccess }) => {
  const notifications = useNotifications();
  const person = usePersonLabels('front_window');
  const createMutation = useCreateLossRecord();

  // Person search state
  const [personSearchInput, setPersonSearchInput] = useState('');
  const [personOptions, setPersonOptions] = useState<PersonOption[]>([]);
  const [personLoading, setPersonLoading] = useState(false);
  const [selectedPerson, setSelectedPerson] = useState<PersonOption | null>(null);

  // HR items state
  const [selectedAssetIds, setSelectedAssetIds] = useState<Set<string>>(new Set());

  // Form state
  const [comments, setComments] = useState('');
  const [includeContaminationMemo, setIncludeContaminationMemo] = useState(false);

  // Document fields modal state
  const [fieldsModalOpen, setFieldsModalOpen] = useState(false);
  const [isLoadingPreview, setIsLoadingPreview] = useState(false);
  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);
  const [previewData, setPreviewData] = useState<DocumentPreviewResponse | null>(null);
  const pendingResultRef = useRef<{ id: string } | null>(null);
  const docWindowRef = useRef<Window | null>(null);
  const assetCountRef = useRef(0);
  const selectedPersonRef = useRef<PersonOption | null>(null);

  // ContaminationMemo fields modal state
  const [memoFieldsModalOpen, setMemoFieldsModalOpen] = useState(false);
  const [memoPreviewData, setMemoPreviewData] = useState<DocumentPreviewResponse | null>(null);
  const [isGeneratingMemo, setIsGeneratingMemo] = useState(false);
  const memoWindowRef = useRef<Window | null>(null);

  // Fetch PHR/THR items for selected person (PnP items excluded)
  const { data: phrData = [], isLoading: phrLoading } = usePHRItems(
    selectedPerson?.id ?? '',
    EXCLUDE_PNP_TYPE_KEEP_NULL,
  );
  const { data: thrData = [], isLoading: thrLoading } = useTHRItems(
    selectedPerson?.id ?? '',
    EXCLUDE_PNP_TYPE_KEEP_NULL,
  );

  const hrItemsLoading = phrLoading || thrLoading;
  const createdTypeLabel = getLossTypeLabel({
    transactionType: 'DD200',
    includeContaminationMemo,
  });

  // Combine PHR and THR items
  const handReceiptItems = useMemo<HandReceiptItem[]>(() => {
    const phrItems: HandReceiptItem[] = phrData
      .filter((item) => item.status?.toLowerCase() !== 'closed')
      .map((item) => ({
        id: item.id,
        asset_id: item.asset_id ?? null,
        item: item.item,
        lin: item.lin ?? null,
        nsn: item.nsn ?? null,
        receiptType: 'PHR' as const,
        issueDate: item.issueDate ?? null,
        actualPrice: item.actualPrice ?? null,
        unitPrice: item.unitPrice ?? null,
      }));

    const thrItems: HandReceiptItem[] = thrData
      .filter((item) => item.status?.toLowerCase() !== 'closed')
      .map((item) => ({
        id: item.id,
        asset_id: item.asset_id ?? null,
        item: item.item,
        lin: item.lin ?? null,
        nsn: item.nsn ?? null,
        receiptType: 'THR' as const,
        issueDate: item.issueDate ?? null,
        actualPrice: item.actualPrice ?? null,
        unitPrice: item.unitPrice ?? null,
      }));

    return [...thrItems, ...phrItems];
  }, [phrData, thrData]);

  // Group hand receipt items by LIN+NSN for consolidated display with quantity
  const groupedHandReceiptItems = useMemo(() => {
    const grouped = new Map<
      string,
      { item: HandReceiptItem; quantity: number; assetIds: string[] }
    >();

    handReceiptItems.forEach((hrItem) => {
      if (!hrItem.asset_id) return;
      const lin = (hrItem.lin ?? '').trim().toUpperCase();
      const nsn = (hrItem.nsn ?? '').trim().toUpperCase();
      const key = nsn ? `${lin}|${nsn}` : `asset:${hrItem.id}`;
      const existing = grouped.get(key);

      if (!existing) {
        grouped.set(key, { item: hrItem, quantity: 1, assetIds: [hrItem.asset_id] });
        return;
      }

      existing.quantity += 1;
      existing.assetIds.push(hrItem.asset_id);
    });

    return Array.from(grouped.values());
  }, [handReceiptItems]);

  // Flat rows for sorting — each grouped row has all base fields plus quantity/assetIds
  const groupedDisplayRows = useMemo(
    () =>
      groupedHandReceiptItems.map((g) => ({
        ...g.item,
        quantity: g.quantity,
        assetIds: g.assetIds,
      })),
    [groupedHandReceiptItems],
  );

  // Get selected hand receipt items, deduplicated by asset_id.
  // The same asset can appear on both PHR and THR; without dedup the
  // duplicate entry inflates quantities and causes items to appear on
  // both page 1 and the continuation sheet of the generated FLIPL.
  const selectedHandReceiptItems = useMemo(() => {
    const byAssetId = new Map<string, HandReceiptItem>();
    for (const item of handReceiptItems) {
      if (!item.asset_id || !selectedAssetIds.has(item.asset_id)) continue;
      const existing = byAssetId.get(item.asset_id);
      if (!existing || (item.nsn && !existing.nsn)) {
        byAssetId.set(item.asset_id, item);
      }
    }
    return Array.from(byAssetId.values());
  }, [handReceiptItems, selectedAssetIds]);

  const {
    sortField: hrSortField,
    sortDirection: hrSortDirection,
    onSort: onHrSort,
    sortedRows: sortedHrItems,
  } = useTableSort(groupedDisplayRows as unknown as Record<string, unknown>[]);

  // Aggregate selected items by LIN+NSN so identical items appear as a single
  // row with a summed quantity instead of duplicate individual rows.
  const aggregatedItems = useMemo(() => {
    const grouped = new Map<string, { item: HandReceiptItem; quantity: number }>();

    selectedHandReceiptItems.forEach((hrItem) => {
      const lin = (hrItem.lin ?? '').trim().toUpperCase();
      const nsn = (hrItem.nsn ?? '').trim().toUpperCase();
      // When NSN is absent, keep rows separate to avoid collapsing unrelated items.
      const key = nsn ? `${lin}|${nsn}` : `asset:${hrItem.id}`;
      const existing = grouped.get(key);

      if (!existing) {
        grouped.set(key, { item: hrItem, quantity: 1 });
        return;
      }

      existing.quantity += 1;
    });

    return Array.from(grouped.values());
  }, [selectedHandReceiptItems]);

  // Reset state when modal closes
  useEffect(() => {
    if (!open) {
      setSelectedPerson(null);
      setPersonSearchInput('');
      setPersonOptions([]);
      setSelectedAssetIds(new Set());
      setComments('');
      setIncludeContaminationMemo(false);
      setFieldsModalOpen(false);
      setPreviewData(null);
      setIsLoadingPreview(false);
      setIsGeneratingPdf(false);
      setMemoFieldsModalOpen(false);
      setMemoPreviewData(null);
      setIsGeneratingMemo(false);
      pendingResultRef.current = null;
      docWindowRef.current = null;
      memoWindowRef.current = null;
    }
  }, [open]);

  // Search for persons
  useEffect(() => {
    if (!personSearchInput.trim() || personSearchInput.length < 2) {
      setPersonOptions([]);
      return;
    }

    const controller = new AbortController();
    setPersonLoading(true);

    const searchPersons = async () => {
      try {
        const results = await entityService.searchEntities(personSearchInput, 'person');
        if (!controller.signal.aborted) {
          setPersonOptions(
            results.map((e) => ({
              id: e.id,
              display_name: e.display_name ?? 'Unknown',
              entity_identifier: e.entity_identifier,
              rank: (e.entity_data?.rank as string) ?? null,
              unit: (e.entity_data?.unit as string) ?? null,
              lastName: (e.entity_data?.last_name as string) ?? null,
              firstName: (e.entity_data?.first_name as string) ?? null,
              middleInitial: (e.entity_data?.middle_initial as string) ?? null,
              ssn: (e.entity_data?.ssn as string) ?? null,
              section: (e.entity_data?.section as string) ?? null,
            })),
          );
        }
      } catch (error) {
        if (!controller.signal.aborted) {
          log.error('Person search failed', { error: String(error) });
        }
      } finally {
        if (!controller.signal.aborted) {
          setPersonLoading(false);
        }
      }
    };

    const debounce = setTimeout(searchPersons, 300);
    return () => {
      clearTimeout(debounce);
      controller.abort();
    };
  }, [personSearchInput]);

  // Handle asset selection
  const handleToggleGroup = useCallback((assetIds: string[]) => {
    setSelectedAssetIds((prev) => {
      const next = new Set(prev);
      const allSelected = assetIds.every((id) => next.has(id));
      if (allSelected) {
        assetIds.forEach((id) => next.delete(id));
      } else {
        assetIds.forEach((id) => next.add(id));
      }
      return next;
    });
  }, []);

  const handleSetGroupCount = useCallback((assetIds: string[], count: number) => {
    const clamped = Math.max(0, Math.min(count, assetIds.length));
    setSelectedAssetIds((prev) => {
      const next = new Set(prev);
      assetIds.forEach((id) => next.delete(id));
      assetIds.slice(0, clamped).forEach((id) => next.add(id));
      return next;
    });
  }, []);

  const handleSelectAll = useCallback(() => {
    const allAssetIds = handReceiptItems
      .filter((item) => item.asset_id)
      .map((item) => item.asset_id!);
    setSelectedAssetIds(new Set(allAssetIds));
  }, [handReceiptItems]);

  const handleDeselectAll = useCallback(() => {
    setSelectedAssetIds(new Set());
  }, []);

  // Step 1: Create loss record, then fetch preview config and show document fields modal
  const handleCreate = useCallback(async () => {
    if (!selectedPerson) {
      notifications.warning(
        `Select ${person.label}`,
        `Please select ${person.article} ${person.lower} first.`,
      );
      return;
    }

    if (selectedAssetIds.size === 0) {
      notifications.warning('Select Assets', 'Please select at least one asset.');
      return;
    }

    // Capture values before any async work for use in callbacks
    assetCountRef.current = selectedAssetIds.size;
    selectedPersonRef.current = selectedPerson;

    let result: { id: string };
    try {
      result = await createMutation.mutateAsync({
        transactionType: 'DD200',
        personId: selectedPerson.id,
        assetIds: Array.from(selectedAssetIds),
        comments: comments.trim() || undefined,
        includeContaminationMemo,
      });
    } catch (error) {
      log.error('Failed to create DD200', { error: String(error) });
      notifications.error(
        'Failed to Create',
        error instanceof Error ? error.message : 'An unexpected error occurred.',
      );
      return;
    }

    // Store the result for document generation
    pendingResultRef.current = result;

    // Build the payload matching the DD200 mapping's dataSource paths
    const yyyymmdd = toYYYYMMDD();

    // Format name as "Last, First, MI" for block 16d
    const nameParts: string[] = [];
    if (selectedPerson.lastName) nameParts.push(selectedPerson.lastName);
    if (selectedPerson.firstName) {
      const first = selectedPerson.firstName;
      const mi = selectedPerson.middleInitial ? ` ${selectedPerson.middleInitial.charAt(0)}.` : '';
      nameParts.push(`${first}${mi}`);
    }
    const formattedName =
      nameParts.length > 0 ? nameParts.join(', ') : (selectedPerson.display_name ?? '');

    // Extract last 4 of SSN from HR person data for DD200 defaults
    const fullSsn = selectedPerson.ssn ?? '';
    const ssnDigits = fullSsn.replace(/\D/g, '');
    const ssnLast4 = ssnDigits.length >= 4 ? ssnDigits.slice(-4) : ssnDigits;

    // Compute items with formatted NSN (includes LIN) and costs
    const items = aggregatedItems.map(({ item, quantity }, idx) => {
      const description = item.item ?? '';
      // Auto-populate unit cost from HR pricing (items 2, 12)
      // Prefer asset actual_price (Buy Program), fall back to catalog unit_price
      const unitCost = item.actualPrice ?? item.unitPrice ?? 0;
      const totalCost = quantity * unitCost;
      // Format NSN as "LIN / NSN" so mapping's single field carries both values (WI#29753)
      const lin = (item.lin ?? '').trim();
      const nsn = (item.nsn ?? '').trim();
      const formattedNsn = lin && nsn ? `${lin} / ${nsn}` : nsn || lin;
      return {
        itemNo: String(idx + 1),
        lin: item.lin ?? '',
        nsn: formattedNsn,
        description,
        quantity,
        unitCost: unitCost > 0 ? unitCost : '',
        totalCost: totalCost > 0 ? totalCost : '',
      };
    });

    // Compute grand total and continuation subtotal (WI#29753)
    const grandTotal = items.reduce((sum, item) => {
      const cost = typeof item.totalCost === 'number' ? item.totalCost : 0;
      return sum + cost;
    }, 0);
    // For DD200, continuationSubtotal is the same as grandTotal (all items on continuation pages)
    const continuationSubtotal = grandTotal;

    const payload: Record<string, unknown> = {
      dateInitiated: yyyymmdd,
      dateDiscovered: yyyymmdd,
      handReceipt: '20',
      items,
      grandTotal,
      continuationSubtotal,
      circumstances: {
        lost: false,
        damaged: false,
        destroyed: false,
        organization: false,
        installation: false,
        iem: true,
        narrative: comments.trim(),
      },
      actionsTaken: '',
      completingIndividual: {
        orgAddress: selectedPerson.unit ?? '',
        name: '',
        dsn: '',
      },
      individualCharged: {
        orgAddress: selectedPerson.unit ?? '',
        name: formattedName,
        dsn: ssnLast4,
        handReceipt: '20',
      },
    };

    // Fetch editable fields from the backend (driven by mapping config in DB)
    setIsLoadingPreview(true);
    try {
      const preview = await documentService.getDocumentPreview({
        documentType: 'DD200',
        payload,
      });
      setIsLoadingPreview(false);
      setPreviewData(preview);
      setFieldsModalOpen(true);
    } catch (previewError) {
      setIsLoadingPreview(false);
      log.warn('DD200 preview unavailable, record was created successfully', {
        error: String(previewError),
      });
      notifications.success(
        `${createdTypeLabel} Created`,
        `${createdTypeLabel} initiated for ${assetCountRef.current} asset(s). Document fields could not be loaded — you can regenerate the form later.`,
      );
      onSuccess?.();
      onClose();
    }
  }, [
    selectedPerson,
    selectedAssetIds,
    aggregatedItems,
    comments,
    includeContaminationMemo,
    createMutation,
    createdTypeLabel,
    notifications,
    onSuccess,
    onClose,
  ]);

  // Step 2: Generate document with user-edited payload
  const handleFieldsConfirm = useCallback(
    async (editedPayload: Record<string, unknown>) => {
      const result = pendingResultRef.current;

      if (!result) return;

      // Pre-open the DD200 window now (user-gesture context to avoid popup blocker).
      const docWindow = preOpenWindow();
      docWindowRef.current = docWindow;

      setIsGeneratingPdf(true);

      // Generate DD200
      let docFailed = false;
      try {
        const docResult = await documentService.generateDocument({
          documentType: 'DD200',
          payload: {
            ...editedPayload,
            relatedEntityId: result.id,
          },
          options: { metadata: { relatedEntityId: result.id } },
        });

        // Record the generated document ID on the loss record (best-effort)
        try {
          await lossRecordService.addGeneratedDocumentId(result.id, docResult.documentId);
        } catch (linkErr) {
          log.warn('Failed to record DD200 document ID on loss record', {
            id: result.id,
            documentId: docResult.documentId,
            error: String(linkErr),
          });
        }

        if (docResult.outputBuffer) {
          const opened = openBase64PdfInWindow(docResult.outputBuffer, docWindow);
          if (!opened && docResult.downloadUrl) {
            const fallbackUrl = documentService.resolveDownloadUrl(docResult.downloadUrl);
            if (fallbackUrl) await openDocumentWithAuth(fallbackUrl);
          }
        } else if (docResult.downloadUrl) {
          const url = documentService.resolveDownloadUrl(docResult.downloadUrl);
          if (url) {
            const opened = await openDocumentWithAuth(url, docWindow);
            if (!opened) {
              log.error('Failed to open DD200 document', { downloadUrl: docResult.downloadUrl });
              docFailed = true;
            }
          } else {
            if (docWindow && !docWindow.closed) docWindow.close();
            log.error('resolveDownloadUrl returned null', { downloadUrl: docResult.downloadUrl });
            docFailed = true;
          }
        } else {
          if (docWindow && !docWindow.closed) docWindow.close();
          log.error('No downloadUrl in DD200 generate response');
          docFailed = true;
        }
      } catch (docError) {
        if (docWindow && !docWindow.closed) docWindow.close();
        log.error('Failed to generate DD 200', { error: String(docError) });
        docFailed = true;
      }

      setIsGeneratingPdf(false);
      setFieldsModalOpen(false);

      // If contamination memo is included, show its field-editing modal next
      if (includeContaminationMemo) {
        // Build memo payload from aggregated items + person data
        const memoItems = aggregatedItems.map(({ item: hrItem, quantity }) => {
          const unitCost = hrItem.actualPrice ?? hrItem.unitPrice ?? 0;
          const totalCost = quantity * unitCost;
          return {
            itemNo: '',
            lin: hrItem.lin ?? '',
            nsn: hrItem.nsn ?? '',
            description: hrItem.item ?? '',
            quantity,
            unitCost: unitCost > 0 ? unitCost : undefined,
            totalCost: totalCost > 0 ? totalCost : undefined,
          };
        });

        const fullSsn = selectedPersonRef.current?.ssn ?? '';
        const ssnDigits = fullSsn.replace(/\D/g, '');
        const smSsnLast4 = ssnDigits.length >= 4 ? ssnDigits.slice(-4) : ssnDigits;

        const memoPayload: Record<string, unknown> = {
          date: toYYYYMMDD(),
          date2: toYYYYMMDD(),
          individual: selectedPersonRef.current?.display_name ?? '',
          smSsn: smSsnLast4,
          smUic: selectedPersonRef.current?.unit ?? '',
          smSection: selectedPersonRef.current?.section ?? '',
          handReceiptNumber: '20',
          items: memoItems,
          relatedEntityId: result.id,
          personId: selectedPersonRef.current?.id ?? '',
          ...(typeof editedPayload.investigationNumber === 'string' &&
            editedPayload.investigationNumber.trim().length > 0 && {
              documentNumber: editedPayload.investigationNumber.trim(),
            }),
        };

        try {
          const memoPreview = await documentService.getDocumentPreview({
            documentType: 'ContaminationMemo',
            payload: memoPayload,
          });
          setMemoPreviewData(memoPreview);
          setMemoFieldsModalOpen(true);
        } catch (previewError) {
          log.warn('ContaminationMemo preview unavailable', { error: String(previewError) });
          if (docFailed) {
            notifications.warning(
              'Document Generation Issue',
              `${createdTypeLabel} created for ${assetCountRef.current} asset(s), but the DD200 could not be opened and the Contamination Memo fields could not be loaded. You can regenerate them later.`,
            );
          } else {
            notifications.warning(
              'Contamination Memo Issue',
              `DD200 generated. Contamination Memo fields could not be loaded — you can regenerate it later.`,
            );
          }
          onSuccess?.();
          onClose();
        }

        // If DD200 failed but we're still showing the memo modal, notify about DD200
        if (docFailed) {
          notifications.warning(
            'DD200 Generation Issue',
            `DD200 could not be opened. You can regenerate it later. Proceeding to Contamination Memo fields.`,
          );
        }
        return;
      }

      // No contamination memo — just report DD200 result and close
      const count = assetCountRef.current;
      if (docFailed) {
        notifications.warning(
          'Document Generation Issue',
          `${createdTypeLabel} created for ${count} asset(s), but the DD200 form could not be opened. You can regenerate it later.`,
        );
      } else {
        notifications.success(
          `${createdTypeLabel} Created`,
          `${createdTypeLabel} initiated for ${count} asset(s). DD200 generated.`,
        );
      }

      onSuccess?.();
      onClose();
    },
    [
      includeContaminationMemo,
      aggregatedItems,
      createdTypeLabel,
      notifications,
      onSuccess,
      onClose,
    ],
  );

  // Step 3: Generate ContaminationMemo with user-edited fields
  const handleMemoFieldsConfirm = useCallback(
    async (editedMemoPayload: Record<string, unknown>) => {
      const result = pendingResultRef.current;
      if (!result) return;

      // Pre-open window in user-gesture context
      const memoWindow = preOpenWindow();
      memoWindowRef.current = memoWindow;

      setIsGeneratingMemo(true);

      let memoFailed = false;
      try {
        const memoResult = await documentService.generateDocument({
          documentType: 'ContaminationMemo',
          payload: editedMemoPayload,
        });

        if (memoResult.outputBuffer) {
          const opened = openBase64PdfInWindow(memoResult.outputBuffer, memoWindow);
          if (!opened && memoResult.downloadUrl) {
            const fallbackUrl = documentService.resolveDownloadUrl(memoResult.downloadUrl);
            if (fallbackUrl) await openDocumentWithAuth(fallbackUrl);
          }
        } else if (memoResult.downloadUrl) {
          const url = documentService.resolveDownloadUrl(memoResult.downloadUrl);
          if (url) {
            const opened = await openDocumentWithAuth(url, memoWindow);
            if (!opened) memoFailed = true;
          } else {
            if (memoWindow && !memoWindow.closed) memoWindow.close();
            memoFailed = true;
          }
        } else {
          if (memoWindow && !memoWindow.closed) memoWindow.close();
          memoFailed = true;
        }
      } catch (memoError) {
        if (memoWindow && !memoWindow.closed) memoWindow.close();
        log.error('Failed to generate Contamination Memo', { error: String(memoError) });
        memoFailed = true;
      }

      setIsGeneratingMemo(false);
      setMemoFieldsModalOpen(false);

      const count = assetCountRef.current;
      if (memoFailed) {
        notifications.warning(
          'Contamination Memo Issue',
          `${createdTypeLabel} created for ${count} asset(s). DD200 generated but the Contamination Memo could not be opened. You can regenerate it later.`,
        );
      } else {
        notifications.success(
          `${createdTypeLabel} Created`,
          `${createdTypeLabel} initiated for ${count} asset(s). DD200 and Contamination Memo generated.`,
        );
      }

      onSuccess?.();
      onClose();
    },
    [createdTypeLabel, notifications, onSuccess, onClose],
  );

  // Skip memo document generation
  const handleMemoFieldsSkip = useCallback(() => {
    const memoWindow = memoWindowRef.current;
    if (memoWindow && !memoWindow.closed) memoWindow.close();
    setMemoFieldsModalOpen(false);

    notifications.success(
      `${createdTypeLabel} Created`,
      `${createdTypeLabel} initiated for ${assetCountRef.current} asset(s). DD200 generated. Contamination Memo skipped.`,
    );

    onSuccess?.();
    onClose();
  }, [createdTypeLabel, notifications, onSuccess, onClose]);

  const handleMemoFieldsClose = useCallback(() => {
    const memoWindow = memoWindowRef.current;
    if (memoWindow && !memoWindow.closed) memoWindow.close();
    setMemoFieldsModalOpen(false);

    notifications.success(
      `${createdTypeLabel} Created`,
      `${createdTypeLabel} initiated for ${assetCountRef.current} asset(s). DD200 generated. Contamination Memo cancelled.`,
    );

    onSuccess?.();
    onClose();
  }, [createdTypeLabel, notifications, onSuccess, onClose]);

  // Skip document generation but still complete the creation
  const handleFieldsSkip = useCallback(() => {
    const docWindow = docWindowRef.current;
    if (docWindow && !docWindow.closed) docWindow.close();

    setFieldsModalOpen(false);

    notifications.success(
      `${createdTypeLabel} Created`,
      `${createdTypeLabel} initiated for ${assetCountRef.current} asset(s). Document generation skipped.`,
    );

    onSuccess?.();
    onClose();
  }, [createdTypeLabel, notifications, onSuccess, onClose]);

  const handleFieldsClose = useCallback(() => {
    const docWindow = docWindowRef.current;
    if (docWindow && !docWindow.closed) docWindow.close();
    setFieldsModalOpen(false);

    // Record was already created, so still notify success
    notifications.success(
      `${createdTypeLabel} Created`,
      `${createdTypeLabel} initiated for ${assetCountRef.current} asset(s). Document generation cancelled.`,
    );

    onSuccess?.();
    onClose();
  }, [createdTypeLabel, notifications, onSuccess, onClose]);

  const canCreate =
    selectedPerson && selectedAssetIds.size > 0 && !createMutation.isPending && !isLoadingPreview;

  return (
    <>
      <Dialog
        open={open && !fieldsModalOpen && !memoFieldsModalOpen}
        onClose={onClose}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Typography variant="h6" component="span">
            Initiate FLIPL
          </Typography>
          <Typography variant="body2" color="text.secondary">
            DD200 - Financial Liability Investigation of Property Loss (FLIPL)
          </Typography>
        </DialogTitle>
        <DialogContent dividers>
          <Stack spacing={3}>
            {/* Step 1: Select Service Member */}
            <Box>
              <Typography variant="subtitle1" fontWeight="medium" gutterBottom>
                1. Select {person.label}
              </Typography>
              <Autocomplete
                options={personOptions}
                getOptionLabel={(option) => {
                  const id = option.entity_identifier
                    ? option.entity_identifier.replace(/^INDV-/, person.individualIdPrefix)
                    : '';
                  return `${option.display_name}${id ? ` (${id})` : ''}`;
                }}
                value={selectedPerson}
                onChange={(_, newValue) => {
                  setSelectedPerson(newValue);
                  setSelectedAssetIds(new Set());
                }}
                inputValue={personSearchInput}
                onInputChange={(_, newInputValue) => setPersonSearchInput(newInputValue)}
                loading={personLoading}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={`Search by name or ${person.individualIdLabel}`}
                    placeholder="Start typing to search..."
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {personLoading ? <CircularProgress color="inherit" size={20} /> : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
                noOptionsText={
                  personSearchInput.length < 2
                    ? 'Type at least 2 characters to search'
                    : `No ${person.lowerPlural} found`
                }
              />
            </Box>

            {/* Step 2: Select Hand Receipt Items */}
            {selectedPerson && (
              <Box>
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    mb: 1,
                  }}
                >
                  <Typography variant="subtitle1" fontWeight="medium">
                    2. Select Hand Receipt Items ({selectedAssetIds.size} selected)
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button size="small" onClick={handleSelectAll} disabled={hrItemsLoading}>
                      Select All
                    </Button>
                    <Button size="small" onClick={handleDeselectAll} disabled={hrItemsLoading}>
                      Deselect All
                    </Button>
                  </Box>
                </Box>

                {hrItemsLoading ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                    <CircularProgress />
                  </Box>
                ) : handReceiptItems.length === 0 ? (
                  <Alert severity="info">
                    No items found on this {person.lower}'s hand receipt.
                  </Alert>
                ) : (
                  <TableContainer sx={{ maxHeight: 300 }}>
                    <Table size="small" stickyHeader>
                      <TableHead>
                        <TableRow>
                          <TableCell padding="checkbox" />
                          <SortableTableCell
                            field="item"
                            sortField={hrSortField}
                            sortDirection={hrSortDirection}
                            onSort={onHrSort}
                          >
                            Item
                          </SortableTableCell>
                          <SortableTableCell
                            field="quantity"
                            sortField={hrSortField}
                            sortDirection={hrSortDirection}
                            onSort={onHrSort}
                          >
                            Qty
                          </SortableTableCell>
                          <SortableTableCell
                            field="lin"
                            sortField={hrSortField}
                            sortDirection={hrSortDirection}
                            onSort={onHrSort}
                          >
                            LIN
                          </SortableTableCell>
                          <SortableTableCell
                            field="nsn"
                            sortField={hrSortField}
                            sortDirection={hrSortDirection}
                            onSort={onHrSort}
                          >
                            NSN
                          </SortableTableCell>
                          <SortableTableCell
                            field="receiptType"
                            sortField={hrSortField}
                            sortDirection={hrSortDirection}
                            onSort={onHrSort}
                          >
                            Type
                          </SortableTableCell>
                          <SortableTableCell
                            field="issueDate"
                            sortField={hrSortField}
                            sortDirection={hrSortDirection}
                            onSort={onHrSort}
                          >
                            Issue Date
                          </SortableTableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {(
                          sortedHrItems as unknown as (HandReceiptItem & {
                            quantity: number;
                            assetIds: string[];
                          })[]
                        ).map((row) => {
                          const selectedCount = row.assetIds.filter((id) =>
                            selectedAssetIds.has(id),
                          ).length;
                          const allSelected = selectedCount === row.assetIds.length;
                          const someSelected = selectedCount > 0 && !allSelected;
                          return (
                            <TableRow
                              key={row.id}
                              hover
                              onClick={() => handleToggleGroup(row.assetIds)}
                              sx={{ cursor: 'pointer' }}
                            >
                              <TableCell padding="checkbox">
                                <Checkbox
                                  checked={allSelected}
                                  indeterminate={someSelected}
                                  onClick={(e) => e.stopPropagation()}
                                  onChange={() => handleToggleGroup(row.assetIds)}
                                  sx={{ cursor: 'pointer' }}
                                />
                              </TableCell>
                              <TableCell>{row.item}</TableCell>
                              <TableCell
                                sx={{ textAlign: 'center' }}
                                onClick={
                                  row.quantity > 1
                                    ? (e: React.MouseEvent) => e.stopPropagation()
                                    : undefined
                                }
                              >
                                {row.quantity > 1 ? (
                                  <Box
                                    sx={{
                                      display: 'inline-flex',
                                      alignItems: 'center',
                                      gap: 0.5,
                                      justifyContent: 'center',
                                    }}
                                  >
                                    <TextField
                                      type="number"
                                      size="small"
                                      variant="standard"
                                      value={selectedCount}
                                      inputProps={{
                                        min: 0,
                                        max: row.quantity,
                                        style: { textAlign: 'center', width: 28, padding: 0 },
                                      }}
                                      onChange={(e) =>
                                        handleSetGroupCount(
                                          row.assetIds,
                                          parseInt(e.target.value) || 0,
                                        )
                                      }
                                      sx={{ width: 36 }}
                                    />
                                    <Typography
                                      variant="caption"
                                      sx={{ whiteSpace: 'nowrap', fontSize: '0.7rem' }}
                                    >
                                      of {row.quantity}
                                    </Typography>
                                  </Box>
                                ) : (
                                  row.quantity
                                )}
                              </TableCell>
                              <TableCell>{row.lin ?? '—'}</TableCell>
                              <TableCell>{row.nsn ?? '—'}</TableCell>
                              <TableCell>{row.receiptType}</TableCell>
                              <TableCell>
                                {row.issueDate ? new Date(row.issueDate).toLocaleDateString() : '—'}
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </Box>
            )}

            {/* Step 3: Loss Details */}
            {selectedPerson && selectedAssetIds.size > 0 && (
              <Box>
                <Typography variant="subtitle1" fontWeight="medium" gutterBottom>
                  3. Initial Loss Details
                </Typography>
                <TextField
                  label="Comments"
                  placeholder="Describe the circumstances of the loss..."
                  multiline
                  rows={3}
                  fullWidth
                  value={comments}
                  onChange={(e) => setComments(e.target.value)}
                />

                <FormControlLabel
                  control={
                    <Checkbox
                      checked={includeContaminationMemo}
                      onChange={(e) => setIncludeContaminationMemo(e.target.checked)}
                    />
                  }
                  label="Generate MFR (Contamination Memo)"
                  sx={{ mt: 2 }}
                />
              </Box>
            )}

            {/* Info about what Create does */}
            {selectedPerson && selectedAssetIds.size > 0 && (
              <Alert severity="info">
                <Typography variant="body2">
                  <strong>Create</strong> will:
                </Typography>
                <ul style={{ margin: '8px 0 0 0', paddingLeft: 20 }}>
                  <li>Create an Asset Loss Management record</li>
                  <li>
                    Open the DD200 form fields for review before generating
                    {includeContaminationMemo ? ' (with Contamination Memo)' : ''}
                  </li>
                  <li>
                    <strong>
                      Remove the selected assets from the {person.lower}'s Hand Receipt
                    </strong>
                  </li>
                </ul>
              </Alert>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button
            variant="outlined"
            onClick={onClose}
            disabled={createMutation.isPending || isLoadingPreview}
          >
            Cancel
          </Button>
          <ViewerGuardedButton
            variant="contained"
            onClick={handleCreate}
            disabled={!canCreate}
            startIcon={
              createMutation.isPending || isLoadingPreview ? (
                <CircularProgress size={16} />
              ) : undefined
            }
          >
            {createMutation.isPending
              ? 'Creating...'
              : isLoadingPreview
                ? 'Loading form...'
                : 'Create'}
          </ViewerGuardedButton>
        </DialogActions>
      </Dialog>

      {/* Document Fields Modal — shown after loss record is created */}
      <DocumentFieldsModal
        open={fieldsModalOpen}
        onClose={handleFieldsClose}
        onConfirm={handleFieldsConfirm}
        onSkip={handleFieldsSkip}
        previewData={previewData}
        isGenerating={isGeneratingPdf}
        title="DD 200 — FLIPL"
        subtitle="Review and fill in any additional fields before generating the document. All fields are optional."
        confirmLabel="Generate Document"
        skipLabel="Skip Document"
        cancelLabel="Close"
      />

      {/* Contamination Memo Fields Modal — shown after DD200 is generated */}
      <DocumentFieldsModal
        open={memoFieldsModalOpen}
        onClose={handleMemoFieldsClose}
        onConfirm={handleMemoFieldsConfirm}
        onSkip={handleMemoFieldsSkip}
        previewData={memoPreviewData}
        isGenerating={isGeneratingMemo}
        title="Contamination Memo (MFR)"
        subtitle="Review and fill in any additional fields before generating the Contamination Memo. Squadron Commander Name and other fields are editable."
        confirmLabel="Generate Memo"
        skipLabel="Skip Memo"
        cancelLabel="Close"
      />
    </>
  );
};
